// File: Componates/BillingSummarySection.jsx
import React from "react";
import PropTypes from "prop-types";
import { getByPath, formatValue } from "./utils/checkoutTableUtils";

/**
 * Pure display component. Every value it shows comes straight from
 * `data` (= RoomCkeckOutDetail.billing_detail, computed entirely on the
 * backend in getCheckOutDetails). No math happens here.
 */
const BillingSummarySection = ({ data, config = [], RoomCkeckOutDetail }) => {
    // taxBreakdown comes from billing_detail.taxBreakdown (set by backend)
    const taxBreakdown = Array.isArray(data?.taxBreakdown) ? data.taxBreakdown : [];
    const parkingTotal = RoomCkeckOutDetail?.parking_detail?.total_amount


    return (
        <>
            {/* Existing Transactions */}
            {data?.transactions?.length > 0 && (
                <div className="col-12 col-lg-6">
                    <div className="table-responsive w-100">
                        <table className="table table-bordered table-hover align-middle mb-0 h-100">
                            <thead className="table-light">
                                <tr>
                                    <th width="20%">Payment Method</th>
                                    <th width="15%" className="text-end">Amount</th>
                                    <th width="20%">Transaction ID</th>
                                    <th width="25%">Date</th>
                                </tr>
                            </thead>

                            <tbody>
                                {data.transactions.map((t) => (
                                    <tr key={t.payment_id}>
                                        <td className="fw-semibold">{t.payment_method_name}</td>
                                        <td className="text-end">{formatValue(t.amount, "currency")}</td>
                                        <td>{t.transaction_id || "-"}</td>
                                        <td>{formatValue(t.transaction_date, "date")}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Billing Summary */}
            <div className="col-12 col-lg-6">
                <div className="table-responsive w-100">
                    <table className="table table-bordered align-middle mb-0 h-100">
                        <thead className="table-light">
                            <tr>
                                <th width="75%">Billing Summary</th>
                                <th width="25%" className="text-end">Amount</th>
                            </tr>
                        </thead>

                        <tbody>
                            {config.map((row) => {
                                // Skip the generic "Tax" row — individual tax rows are
                                // injected dynamically from taxBreakdown just before
                                // the "Total Amount" row below.

                                // Inject per-tax breakdown rows immediately before "Total Amount"
                                if (row.emphasize && row.key === "total_amount") {
                                    return (
                                        <React.Fragment key={row.key}>
                                            {Number(parkingTotal) > 0 && (
                                                <tr>
                                                    <td>Parking Total</td>
                                                    <td className="text-end fw-semibold">
                                                        {formatValue(parkingTotal, "currency")}
                                                    </td>
                                                </tr>
                                            )}

                                            {taxBreakdown.map((tax) => (
                                                <tr key={`tax_${tax.tax_id}`}>
                                                    <td>
                                                        {tax.tax_name}
                                                        {tax.rate ? ` (${tax.rate}%)` : ""}
                                                    </td>
                                                    <td className="text-end fw-semibold">
                                                        {formatValue(tax.amount, "currency")}
                                                    </td>
                                                </tr>
                                            ))}

                                            <tr className="table-light fw-bold">
                                                <td>{row.label}</td>
                                                <td className="text-end fw-semibold">
                                                    {formatValue(getByPath(data, row.key), row.format)}
                                                </td>
                                            </tr>
                                        </React.Fragment>
                                    );
                                }


                                let value = getByPath(data, row.key);
                                let label = row.label;

                                // Sign-aware rows (e.g. balance_due): the LABEL carries the
                                // meaning ("Due Amount" vs "Balance"). The displayed number
                                // is always positive — never show a minus sign here.
                                if (row.signAware) {
                                    const num = Number(value || 0);

                                    if (num < 0) {
                                        label = row.negativeLabel ?? row.label;
                                    } else if (num > 0) {
                                        label = row.positiveLabel ?? row.label;
                                    } else {
                                        label = row.negativeLabel ?? row.label;
                                    }

                                    value = Math.abs(num);
                                }

                                if (row.hideIfZero && Number(value || 0) === 0) return null;

                                const percentValue = row.percentKey ? getByPath(data, row.percentKey) : null;

                                return (
                                    <tr
                                        key={row.key}
                                        className={row.emphasize ? "table-light fw-bold" : ""}
                                    >
                                        <td>
                                            {label}
                                            {percentValue ? ` (${percentValue}%)` : ""}
                                        </td>

                                        <td className="text-end fw-semibold">
                                            {formatValue(value, row.format)}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </>
    )

};

BillingSummarySection.propTypes = {
    data: PropTypes.object,
    config: PropTypes.array
};

export default BillingSummarySection;